/* eslint-disable no-console */
import React from 'react';
import Select from 'react-select';
import { Badge } from 'reactstrap';

import { Option, BaseFilter, BaseFilterConfigParams } from './FilterTypes';
import SelectedFilterBadge from './SelectedFiltersBar/SelectedFilterBadge';

/**
 * Render styles for multi-select filters.
 */
export enum MultiSelectRenderStyle {
  Dropdown = 'dropdown',
  Badges = 'badges',
}
/**
 * Multi-select filter configuration.
 */
export class MultiSelectFilterConfig extends BaseFilter {
  options: Option[];
  renderStyle?: MultiSelectRenderStyle;

  constructor(
    params: BaseFilterConfigParams & {
      options: Option[];
      renderStyle?: MultiSelectRenderStyle;
    },
  ) {
    super({ ...params });
    this.options = params.options;
    this.renderStyle = params.renderStyle;
  }

  render(onSelectedValueChange: () => void) {
    return (
      <MultiSelect
        key={this.id}
        config={this}
        onSelectedValueChange={onSelectedValueChange}
      />
    );
  }

  getSelectedOptionLabels(): string[] {
    const selectedValues = this.getSelectedValues();
    return selectedValues.map((value) => {
      const option = this.options.find((option) => option.value === value);
      return option ? option.label : value;
    });
  }

  renderFilterBadge(onSelectedValueChange: () => void): JSX.Element | null {
    const optionLabels = this.getSelectedOptionLabels();

    return (
      <SelectedFilterBadge
        key={this.id}
        label={this.label}
        values={optionLabels}
        onRemove={() => {
          this.resetSelectionToDefault();
          onSelectedValueChange();
        }}
      />
    );
  }
}

interface MultiSelectProps {
  config: MultiSelectFilterConfig;
  onSelectedValueChange: () => void;
  menuPortalTarget?: HTMLElement | null;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
  config,
  onSelectedValueChange,
  menuPortalTarget = document.body,
}) => {
  // Use the getSelectedValues method to get the selected values
  const { options, renderStyle } = config;

  /**
   * Updates the selected values based on the option attributes.
   * If the option has `deselectOthers` set to true, it will deselect all other options.
   * If the option has `deselectSpecific`, it will deselect only the specified options + deselect options with `deselectOthers`.
   * If any other option is selected, it will deselect options with `deselectOthers`.
   * @param selectedValues The current selected values.
   * @param option The option that was clicked.
   * @returns The updated selected values.
   */
  const updateSelectedValues = (selectedValues: string[], option: Option) => {
    let updatedValues: string[] = [];
    const isSelected = selectedValues.includes(option.value as string);

    if (option.deselectOthers) {
      // If the option has `deselectOthers`, deselect all other options
      updatedValues = isSelected ? [] : [option.value as string];
    } else if (option.deselectSpecific) {
      // If the option has `deselectSpecific`, deselect only the specified options + deselect options with `deselectOthers`
      updatedValues = isSelected
        ? selectedValues.filter((v) => v !== option.value)
        : [
            ...selectedValues
              .filter((v) => !option.deselectSpecific?.includes(v))
              // + deselect options with `deselectOthers`
              .filter(
                (v) => !options.find((o) => o.deselectOthers && v === o.value),
              ),
            option.value as string,
          ];
    } else {
      // If any other option is selected, deselect options with `deselectOthers`
      updatedValues = isSelected
        ? selectedValues.filter((v) => v !== option.value)
        : [
            // Filter out any options that have `deselectOthers` set to true
            ...selectedValues.filter(
              (v) => !options.find((o) => o.deselectOthers && v === o.value),
            ),
            option.value as string,
          ];
    }

    return updatedValues;
  };

  /**
   * Handles the click event for an option.
   * @param option The option that was clicked.
   */
  const handleOptionClick = (option: Option) => {
    const selectedValues = config.getSelectedValues();
    const updatedValues = updateSelectedValues(selectedValues, option);

    // Update the current selected values in the config
    config.currentSelectedValues = updatedValues;

    // Log the updated values for debugging
    // console.log({
    //   msg: 'Log:',
    //   updatedValues,
    //   selectedValues,
    //   options,
    //   'config.currentSelectedValues': config.getSelectedValues(),
    // });

    // Trigger onChange callback if provided
    onSelectedValueChange?.(); // Pass updated value to parent
  };

  /**
   * Handles the change event for the select dropdown.
   * @param selectedOptions The selected options.
   */
  const handleSelectOptionChange = (selectedOptions: Option[]) => {
    // Handle "Clear All" scenario
    if (selectedOptions.length === 0) {
      config.currentSelectedValues = [];
      onSelectedValueChange?.();
      return;
    }

    const previousSelectedValues = config.getSelectedValues();
    // identify the new option that is selected or deselected
    // we have the previous selected values and the new selected options
    // we need to find the difference between the two
    // if the selected option is already in the selected values, we need to remove it
    // if the selected option is not in the selected values, we need to add it
    const newOptions = selectedOptions.filter(
      (option) => !previousSelectedValues.includes(option.value as string),
    );
    const deselectedOptions = previousSelectedValues
      .filter(
        (value) => !selectedOptions.some((option) => option.value === value),
      )
      .map((value) => options.find((option) => option.value === value))
      .filter((option): option is Option => option !== undefined);

    const updatedOptions = [...newOptions, ...deselectedOptions];

    let updatedValues: string[] = [];

    updatedOptions.forEach((option) => {
      updatedValues = updateSelectedValues(previousSelectedValues, option);
    });

    // Update the current selected values in the config
    config.currentSelectedValues = updatedValues;

    // Log the updated values for debugging
    // console.log({
    //   msg: 'handleSelectOptionChange Log:',
    //   selectedOptions,
    //   updatedValues,
    //   previousSelectedValues: previousSelectedValues,
    //   options,
    //   'config.currentSelectedValues': config.getSelectedValues(),
    // });

    // Trigger onChange callback if provided
    onSelectedValueChange?.(); // Pass updated value to parent
  };

  /**
   * Renders the options as badges.
   * Each badge represents an option and can be clicked to select/deselect the option.
   */
  const renderBadges = () => (
    <div className="d-flex flex-wrap g-0">
      {options.map((option) => (
        <Badge
          key={String(option.value)}
          color={
            config.getSelectedValues().includes(String(option.value))
              ? 'primary'
              : 'secondary'
          }
          pill
          className="cursor-pointer mb-1 px-2 py-2"
          style={{
            fontSize: '0.80rem',
            cursor: 'pointer',
            fontWeight: 'normal',
          }}
          onClick={() => handleOptionClick(option)}
        >
          {option.label}
        </Badge>
      ))}
    </div>
  );

  /**
   * Renders the options as a dropdown menu using react-select.
   * Each dropdown item represents an option and can be clicked to select/deselect the option.
   */
  const renderDropdown = () => {
    const selectedOptions = options.filter((option) =>
      config.getSelectedValues().includes(option.value),
    );

    return (
      <Select
        key={`multi-select-${config.id}`}
        isMulti
        value={selectedOptions}
        onChange={(selectedOptions) => {
          handleSelectOptionChange(selectedOptions as Option[]);
        }}
        options={options}
        menuPortalTarget={menuPortalTarget}
        closeMenuOnSelect={false}
        hideSelectedOptions={true}
        placeholder={config.label}
        styles={{
          control: (base) => ({
            ...base,
            cursor: 'pointer',
          }),
          option: (base) => ({
            ...base,
            cursor: 'pointer',
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
      />
    );
  };

  // Render the component based on the specified render style
  return renderStyle === MultiSelectRenderStyle.Badges
    ? renderBadges()
    : renderDropdown();
};

export default MultiSelect;
